import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { handlePlanLimit } from "@/lib/planLimit";
import DashboardLayout from "@/components/DashboardLayout";
import { Lightning, Plus, EnvelopeOpen, X, ArrowRight, CheckCircle, GoogleLogo, Plugs } from "@phosphor-icons/react";

const CATEGORY_TAG = {
  lead: "tag tag-lead", support: "tag tag-support", spam: "tag tag-spam"
};

export default function EmailModule() {
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ sender: "", subject: "", body: "" });
  const [batchBusy, setBatchBusy] = useState(false);
  const [showGmail, setShowGmail] = useState(false);
  const [waitEmail, setWaitEmail] = useState("");
  const [waitBusy, setWaitBusy] = useState(false);
  const [waitDone, setWaitDone] = useState(false);
  const [waitError, setWaitError] = useState("");

  const refresh = async () => {
    const { data } = await api.get("/emails");
    setEmails(data);
  };
  useEffect(() => { refresh(); }, []);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    setWaitBusy(true); setWaitError("");
    try {
      await api.post("/waitlist", { email: waitEmail.trim().toLowerCase(), interest: "gmail" });
      setWaitDone(true);
    } catch (err) {
      setWaitError(err.response?.data?.detail || "Couldn't join the waitlist. Try again.");
    } finally { setWaitBusy(false); }
  };

  const processOne = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/emails/${id}/process`);
      setSelected(data);
      await refresh();
    } catch (err) {
      if (!handlePlanLimit(err)) toast.error(err.response?.data?.detail || "Could not process email.");
    } finally { setBusy(false); }
  };
  const processAll = async () => {
    setBatchBusy(true);
    try {
      const { data } = await api.post("/emails/process-all");
      if (data?.skipped_quota > 0) {
        toast.warning(`Triaged ${data.processed} emails. ${data.skipped_quota} were skipped — your plan limit was reached.`, {
          action: { label: "Upgrade", onClick: () => { window.location.href = "/pricing"; } },
          duration: 9000,
        });
      } else if ((data?.processed || 0) > 0) {
        toast.success(`Triaged ${data.processed} email${data.processed > 1 ? "s" : ""}.`);
      }
      await refresh();
    } catch (err) {
      if (!handlePlanLimit(err)) toast.error(err.response?.data?.detail || "Batch failed.");
    } finally { setBatchBusy(false); }
  };
  const addEmail = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/emails", form);
    setShowAdd(false);
    setForm({ sender: "", subject: "", body: "" });
    setEmails((es) => [data, ...es]);
  };

  return (
    <DashboardLayout
      title="Email Automation"
      actions={
        <>
          <button onClick={() => setShowGmail(true)} className="btn-secondary text-sm" data-testid="connect-gmail-btn">
            <GoogleLogo size={16} weight="bold" /> Connect Gmail
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-secondary text-sm" data-testid="add-email-btn">
            <Plus size={16} weight="bold" /> Paste email
          </button>
          <button onClick={processAll} disabled={batchBusy} className="btn-primary text-sm" data-testid="process-all-emails">
            <Lightning size={16} weight="fill" /> {batchBusy ? "Triaging…" : "Triage all"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        {/* Inbox list */}
        <div className="col-span-12 lg:col-span-5 cell-strong bg-white">
          <div className="px-4 py-3 border-b border-[var(--line)] flex items-center justify-between">
            <div className="overline text-[var(--ink-soft)]">/ Inbox · {emails.length}</div>
            <span className="text-xs text-[var(--ink-soft)] font-mono-rc">{emails.filter(e => e.processed).length} processed</span>
          </div>
          <ul className="divide-y divide-[var(--line)] max-h-[640px] overflow-auto">
            {emails.map((e) => (
              <li
                key={e.email_id}
                onClick={() => setSelected(e)}
                className={`p-4 cursor-pointer hover:bg-[var(--bg-soft)] ${selected?.email_id === e.email_id ? "bg-[var(--bg-soft)]" : ""}`}
                data-testid={`email-row-${e.email_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-mono-rc text-[var(--ink-soft)] truncate">{e.sender}</div>
                  {e.category && <span className={CATEGORY_TAG[e.category] || "tag"}>{e.category}</span>}
                </div>
                <div className="text-base font-medium mt-1 truncate">{e.subject}</div>
                <div className="text-sm text-[var(--ink-soft)] mt-1 line-clamp-2">{e.body}</div>
                {e.priority && <div className="text-xs mt-1 font-mono-rc">priority: {e.priority}</div>}
              </li>
            ))}
            {emails.length === 0 && <li className="p-8 text-center text-[var(--ink-soft)] text-sm">Inbox is empty.</li>}
          </ul>
        </div>

        {/* Detail */}
        <div className="col-span-12 lg:col-span-7">
          {!selected ? (
            <div className="cell-strong bg-white p-12 text-center">
              <EnvelopeOpen size={48} weight="duotone" className="mx-auto text-[var(--brand)]" />
              <div className="font-display text-2xl mt-4">Pick an email</div>
              <div className="text-[var(--ink-soft)] mt-2 max-w-md mx-auto">
                Select any email from the inbox. Robocope will categorize it, prioritize it, and draft a reply you can send in one click.
              </div>
            </div>
          ) : (
            <div className="cell-strong bg-white p-6">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="overline text-[var(--ink-soft)]">From</div>
                  <div className="font-mono-rc text-sm">{selected.sender}</div>
                  <h2 className="font-display text-2xl mt-3 tracking-tight">{selected.subject}</h2>
                </div>
                {selected.category && <span className={CATEGORY_TAG[selected.category] || "tag"} data-testid="email-category">{selected.category}</span>}
              </div>

              <pre className="mt-4 p-4 bg-[var(--bg-soft)] whitespace-pre-wrap text-sm font-body" data-testid="email-body">
                {selected.body}
              </pre>

              {selected.processed ? (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Mini label="Priority" value={selected.priority || "—"} />
                    <Mini label="Confidence" value={selected.confidence != null ? `${Math.round(selected.confidence * 100)}%` : "—"} />
                    <Mini label="Action" value={selected.suggested_action || "—"} />
                  </div>
                  {selected.summary && (
                    <div className="cell p-4 bg-[var(--bg-soft)]">
                      <div className="overline text-[var(--ink-soft)]">Summary</div>
                      <div className="text-sm mt-1">{selected.summary}</div>
                    </div>
                  )}
                  <div>
                    <div className="overline text-[var(--ink-soft)] mb-2">Drafted reply · ready to send</div>
                    <textarea
                      className="input-rc h-40"
                      defaultValue={selected.draft_reply}
                      data-testid="draft-reply-textarea"
                    />
                    <div className="flex items-center gap-3 mt-3">
                      <button className="btn-primary text-sm" data-testid="approve-send-btn">
                        <CheckCircle size={16} weight="fill" /> Approve & send
                      </button>
                      <button className="btn-secondary text-sm" data-testid="regenerate-btn" onClick={() => processOne(selected.email_id)}>
                        Regenerate
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 cell p-6 text-center bg-[var(--bg-soft)]">
                  <p className="text-sm text-[var(--ink-soft)] mb-3">Robocope hasn't processed this one yet.</p>
                  <button onClick={() => processOne(selected.email_id)} disabled={busy} className="btn-primary" data-testid="process-email-btn">
                    {busy ? "Working…" : "Categorize & draft reply"}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={addEmail} className="cell-strong bg-white p-6 w-full max-w-lg" data-testid="add-email-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">Paste an email</div>
              <button type="button" onClick={() => setShowAdd(false)}><X size={20} weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <input className="input-rc" required placeholder="From: name@example.com" value={form.sender} onChange={(e) => setForm({...form, sender: e.target.value})} data-testid="email-sender-input" />
              <input className="input-rc" required placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="email-subject-input" />
              <textarea className="input-rc h-32" required placeholder="Email body…" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="email-body-input" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" data-testid="submit-email-btn">Add to inbox</button>
            </div>
          </form>
        </div>
      )}

      {/* Gmail Coming Soon modal */}
      {showGmail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowGmail(false)}>
          <div onClick={(e) => e.stopPropagation()} className="cell-strong bg-white p-8 w-full max-w-lg" data-testid="gmail-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[var(--accent)] flex items-center justify-center border border-[var(--ink)]">
                <Plugs size={24} weight="duotone" />
              </div>
              <div>
                <div className="overline text-[var(--ink-soft)]">/ Coming soon</div>
                <div className="font-display text-2xl">Connect your Gmail</div>
              </div>
            </div>
            <p className="text-[var(--ink-soft)] mb-4">
              Robocope will pull your latest emails directly from Gmail, triage them with AI, and let you approve replies in one click — without leaving the dashboard.
            </p>
            <div className="cell p-4 bg-[var(--bg-soft)] mb-4">
              <div className="overline text-[var(--ink-soft)] mb-2">What unlocks when you connect</div>
              <ul className="text-sm space-y-1.5">
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--brand)] mt-0.5 shrink-0" /> Live inbox sync (no copy-pasting)</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--brand)] mt-0.5 shrink-0" /> One-click send of approved drafts</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--brand)] mt-0.5 shrink-0" /> Auto-label emails by category</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--brand)] mt-0.5 shrink-0" /> Background triage 24/7</li>
              </ul>
            </div>

            {waitDone ? (
              <div className="cell-strong bg-[var(--accent)] p-4 mb-4 flex items-center gap-2" data-testid="waitlist-success">
                <CheckCircle size={22} weight="fill" />
                <div>
                  <div className="font-medium">You're on the list.</div>
                  <div className="text-xs">We'll email you the moment Gmail is live.</div>
                </div>
              </div>
            ) : (
              <form onSubmit={submitWaitlist} className="mb-4" data-testid="gmail-waitlist-form">
                <div className="overline text-[var(--ink-soft)] mb-2">Get notified the moment it's live</div>
                <div className="flex gap-2">
                  <input
                    type="email" required
                    value={waitEmail}
                    onChange={(e) => setWaitEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="input-rc flex-1"
                    data-testid="waitlist-email-input"
                  />
                  <button type="submit" disabled={waitBusy} className="btn-primary text-sm" data-testid="waitlist-submit-btn">
                    {waitBusy ? "Adding…" : "Notify me"}
                  </button>
                </div>
                {waitError && <div className="mt-2 text-xs text-red-600" data-testid="waitlist-error">{String(waitError)}</div>}
              </form>
            )}

            <div className="flex justify-end">
              <button onClick={() => setShowGmail(false)} className="btn-secondary text-sm" data-testid="gmail-close-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Mini({ label, value }) {
  return (
    <div className="cell p-3">
      <div className="overline text-[var(--ink-soft)]">{label}</div>
      <div className="font-display text-lg mt-1">{value}</div>
    </div>
  );
}
